import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search } from 'lucide-react';
import StatusSummaryCards from './StatusSummaryCards';

/**
 * Search and Filter Components
 * Handles search, filtering, sorting, and view mode controls
 */
const SearchAndFilters = ({
  searchQuery = '',
  onSearchChange,
  sortBy = 'date-added',
  onSortChange,
  isLoading = false,
  onAddCamp,
  activeTab = 'all',
  onStatusClick,
}) => {

  // Filter options (stable so sortOptions reference doesn't change)
  const sortOptions = [
    { value: 'date-added', label: 'Date Added' },
    { value: 'camp-date', label: 'Camp Date' },
    { value: 'alphabetical', label: 'Alphabetical' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' }
  ];

  // Shared search input props so the controlled input isn't recreated across layouts
  const searchInputProps = {
    type: 'search',
    placeholder: 'Search programs...',
    value: searchQuery,
    onChange: (e) => onSearchChange(e.target.value),
    disabled: isLoading
  };

  return (
    <div className="space-y-4">
      {/* Desktop Layout - search left (flex), buttons right-aligned */}
      <div className="hidden lg:flex lg:items-center lg:gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            {...searchInputProps}
            className="pl-10 pr-4 bg-white w-full"
          />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Select value={sortBy} onValueChange={onSortChange} disabled={isLoading}>
            <SelectTrigger className="w-44 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onAddCamp && (
            <Button
              onClick={onAddCamp}
              className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap"
              disabled={isLoading}
            >
              Add a Camp
            </Button>
          )}
          <StatusSummaryCards
            activeTab={activeTab}
            onStatusClick={onStatusClick}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Tablet Layout - search full width, buttons right-aligned on second row */}
      <div className="hidden md:flex md:flex-col lg:hidden gap-3">
        <div className="relative min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            {...searchInputProps}
            className="pl-10 pr-4 bg-white w-full"
          />
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={onSortChange} disabled={isLoading}>
            <SelectTrigger className="w-44 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onAddCamp && (
            <Button
              onClick={onAddCamp}
              className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap"
              disabled={isLoading}
            >
              Add a Camp
            </Button>
          )}
          <StatusSummaryCards
            activeTab={activeTab}
            onStatusClick={onStatusClick}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Mobile Layout - search takes remaining space, buttons right (flex-shrink-0) */}
      <div className="md:hidden space-y-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              {...searchInputProps}
              className="pl-10 pr-4 bg-white w-full min-w-0"
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onAddCamp && (
              <Button
                onClick={onAddCamp}
                className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap"
                disabled={isLoading}
              >
                Add a Camp
              </Button>
            )}
            <StatusSummaryCards
              activeTab={activeTab}
              onStatusClick={onStatusClick}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters;
